import React from 'react';
import { Link } from 'react-router-dom';
import {
  Snowflake,
  Flame,
  Thermometer,
  AlertTriangle,
  Building2,
  Wind,
  UtensilsCrossed,
  ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';
import { services } from '../data/mock';

const iconMap = {
  Snowflake,
  Flame,
  Thermometer,
  AlertTriangle,
  Building2,
  Wind,
  UtensilsCrossed
};

const ServicesSection = ({ onNavigate }) => {
  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="romano-badge mb-4 inline-block">Our Services</span>
          <h2 className="romano-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#622B62] mb-4">
            Expert HVAC Solutions
          </h2>
          <div className="section-divider mx-auto mb-6" />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            From emergency repairs to routine maintenance, we provide comprehensive
            heating and cooling services tailored to your needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            
            // Determine the link path based on service title
            const serviceLinkMap = {
              "AC Tune-Up": "/services/ac-tune-up",
              "Heating Maintenance": "/services/heating-maintenance",
              "Air Duct Cleaning": "/services/air-duct-cleaning",
              "Emergency Services": "/services/emergency",
              "Thermostat Installation": "/services/thermostat-installation",
              "Restaurant Equipment Repair": "/services/restaurant-equipment"
            };
            
            const linkPath = serviceLinkMap[service.title] || null;
            
            return (
              <div
                key={service.id}
                className="service-card bg-[#FDFCF7] rounded-2xl p-8 border border-gray-100 group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#622B62] flex items-center justify-center mb-6 group-hover:bg-[#DA9C3A] transition-colors duration-300">
                  {IconComponent && (
                    <IconComponent size={28} className="text-white" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-[#622B62] mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-5 leading-relaxed">
                  {service.description}
                </p>
                {linkPath ? (
                  <Link
                    to={linkPath}
                    className="inline-flex items-center text-[#DA9C3A] font-semibold hover:text-[#C48A2A] transition-colors group/btn"
                  >
                    Learn More
                    <ArrowRight
                      size={18}
                      className="ml-2 group-hover/btn:translate-x-1 transition-transform"
                    />
                  </Link>
                ) : (
                  <button
                    onClick={() => onNavigate('contact')}
                    className="inline-flex items-center text-[#DA9C3A] font-semibold hover:text-[#C48A2A] transition-colors group/btn"
                  >
                    Learn More
                    <ArrowRight
                      size={18}
                      className="ml-2 group-hover/btn:translate-x-1 transition-transform"
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-[#622B62] rounded-3xl p-8 sm:p-12 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
          
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h3 className="romano-heading text-2xl sm:text-3xl font-bold text-white mb-3">
                Need Emergency HVAC Service?
              </h3>
              <p className="text-white/80 text-lg">
                We're available 24/7 for all your heating and cooling emergencies.
              </p>
            </div>
            <a href="tel:916-274-9021">
              <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-10 py-6 text-lg rounded-full shadow-xl whitespace-nowrap">
                Call Now: 916-274-9021
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
